import React from "react"
import PropTypes from "prop-types"
import { graphql } from "gatsby"

import ThemeSwitcher from "../utils/ThemeSwitcher"
import Link from "../utils/Link"
import SocialLink from "../utils/SocialLink"
import Image from "../utils/Image"

import Box from "../atoms/Box"
import Footer from "../atoms/Footer"
import Flex from "../atoms/Flex"
import { Paragraph } from "../atoms/Typography"
import StandardPadding from "../atoms/StandardPadding"
import NavLink from "../atoms/NavLink"
import MorphicBadge from "../atoms/MorphicBadge"

import Newsletter from "../molecules/Newsletter"

const FooterMinimal = ({
  theme,
  minimalTheme,
  bg,
  logo,
  bgImage,
  minimalOverlayOpacity,
  overlayOpacity,
  alignment = "center",
  text,
  links,
  lastSectionStyling,
  mt,
  hideMorphicBadget,
  morphicBadgeTier,
  minimalMorphicBadgeTier,
  badgeBackground,
  minimalBadgeBackground,
  socialLinks,
  socialLinkStyles,
  useNewsletter,
  newsletter,
  newsletterPosition = "left",
  minimalNewsletterPosition = "left",
  footerWidth,
  minimalFooterWidth,
  ...rest
}) => {
  if (!alignment) alignment = "center"

  const formPosition = newsletterPosition || minimalNewsletterPosition
  const socialStylesObj =
    socialLinkStyles === undefined || socialLinkStyles === null
      ? {
          color: "primary",
          theme: "highlight",
          bg: 3,
          iconStyle: "solid",
        }
      : socialLinkStyles
  const noSocials = socialLinks?.length === 0 || socialLinks === undefined
  const marginTop =
    lastSectionStyling?.sectionBreak === "slope" ? "-160px" : null
  const pt = lastSectionStyling?.sectionBreak === "slope" ? "157px" : null
  const logoPosition = alignment === "left" ? "left center" : "center center"

  return (
    <ThemeSwitcher className="footer" theme={theme || minimalTheme}>
      <Footer mt={mt ? mt : marginTop} zIndex={50} position="relative">
        <StandardPadding
          py={null}
          pt={pt ? pt : 6}
          pb={6}
          px={
            footerWidth === "fullWidth" || minimalFooterWidth === "fullWidth"
              ? 5
              : { _: 5, lg: 7, xl: 4 }
          }
          maxWidth={
            footerWidth === "fullWidth" || minimalFooterWidth === "fullWidth"
              ? "100%"
              : 70
          }
          {...rest}
        >
          <Flex
            flexDirection={alignment === "left" ? "row" : "column"}
            flexWrap={"wrap"}
          >
            <Flex
              zIndex={10}
              flexDirection="column"
              alignItems={alignment === "left" ? "flex-start" : "center"}
              maxWidth={
                footerWidth === "fullWidth" ||
                minimalFooterWidth === "fullWidth"
                  ? "70%"
                  : 40
              }
              mr={"auto"}
              ml={alignment === "left" ? 0 : "auto"}
            >
              {logo && (
                <Box width={{ _: 9, md: 11 }} height={3} mb={3}>
                  <Link aria-label="Go to home page link" to="/">
                    <Image
                      alternativeText="Footer Logo"
                      {...logo}
                      style={{ height: "100%" }}
                      imgStyle={{
                        objectFit: "contain",
                        objectPosition: logoPosition,
                      }}
                    />
                  </Link>
                </Box>
              )}
              {links?.length > 0 && (
                <Flex
                  flexWrap="wrap"
                  justifyContent={
                    alignment === "center" ? "center" : "flex-start"
                  }
                  alignItems={"center"}
                >
                  {links.map((link, i) => (
                    <NavLink
                      mr={6}
                      ml={alignment !== "left" ? 4 : 0}
                      my={3}
                      {...link}
                      key={i}
                      display="inline"
                      variant="footerLink"
                    />
                  ))}
                </Flex>
              )}
              {!noSocials && (
                <Box className="social" mt={2} mb={4}>
                  {socialLinks?.map(socialLink => (
                    <SocialLink {...socialLink} {...socialStylesObj} mr={4} />
                  ))}
                </Box>
              )}
              {((useNewsletter && newsletter && formPosition === "left") ||
                (useNewsletter && newsletter && alignment === "center")) && (
                <Box mb={alignment === "center" ? 4 : 0}>
                  <Newsletter
                    integration={newsletter?.integration}
                    formStyle={newsletter?.formStyle}
                    formWidth={newsletter?.formWidth}
                    title={newsletter?.title}
                    titleAlignment={newsletter?.titleAlignment}
                    boxShadow={newsletter?.boxShadow}
                    borderRadius={newsletter?.borderRadius}
                  />
                </Box>
              )}
              {text && alignment === "center" && (
                <Paragraph variant="footerLink" as="div" textAlign="center">
                  {text}
                </Paragraph>
              )}
              {!hideMorphicBadget && alignment === "center" && (
                <MorphicBadge
                  morphicBadgeTier={morphicBadgeTier || minimalMorphicBadgeTier}
                  badgeBackground={badgeBackground || minimalBadgeBackground}
                  mt={3}
                />
              )}
            </Flex>
            {alignment === "left" && (
              <Flex
                zIndex={10}
                flexDirection={"column"}
                alignItems={{ _: "flex-start", lg: "flex-end" }}
              >
                {useNewsletter && newsletter && formPosition === "right" && (
                  <Box
                    my={4}
                    display={{
                      _: "none",
                      sm: "none",
                      md: "none",
                      lg: "block",
                    }}
                  >
                    <Newsletter
                      integration={newsletter?.integration}
                      formStyle={newsletter?.formStyle}
                      formWidth={newsletter?.formWidth}
                      title={newsletter?.title}
                      titleAlignment={newsletter?.titleAlignment}
                      boxShadow={newsletter?.boxShadow}
                      borderRadius={newsletter?.borderRadius}
                    />
                  </Box>
                )}
                {text && (
                  <Paragraph
                    variant="footerLink"
                    as="div"
                    textAlign="center"
                    mt={3}
                  >
                    {text}
                  </Paragraph>
                )}
                {!hideMorphicBadget && alignment === "left" && (
                  <MorphicBadge
                    morphicBadgeTier={
                      morphicBadgeTier || minimalMorphicBadgeTier
                    }
                    badgeBackground={badgeBackground || minimalBadgeBackground}
                    mt={3}
                  />
                )}
              </Flex>
            )}
          </Flex>
        </StandardPadding>
        <Box
          position="absolute"
          zIndex={-1}
          top="0"
          right="0"
          bottom="0"
          left="0"
          bg={`background.${bg}`}
          overflow="hidden"
        >
          {bgImage && (
            <Image
              alternativeText="Background Image"
              {...bgImage}
              style={{
                height: "100%",
                width: "100%",
              }}
            />
          )}
        </Box>
        <Box
          position="absolute"
          zIndex={0}
          top="0"
          right="0"
          bottom="0"
          left="0"
          bg={`background.${bg}`}
          opacity={overlayOpacity || minimalOverlayOpacity || 0}
        />
      </Footer>
    </ThemeSwitcher>
  )
}

export default FooterMinimal

FooterMinimal.propTypes = {
  theme: PropTypes.oneOf(["default", "light", "dark", "highlight"]),
  minimalTheme: PropTypes.oneOf(["default", "light", "dark", "highlight"]),
  bg: PropTypes.number.isRequired,
  text: PropTypes.string,
  links: PropTypes.arrayOf(PropTypes.shape(Link.strapiProps)),
  morphicBadgeTier: PropTypes.oneOf(["morphic", "enterprise"]),
  badgeBackground: PropTypes.oneOf(["white", "transparent"]),
}

export const query = graphql`
  fragment FooterMinimal on STRAPI__COMPONENT_FOOTERS_MINIMAL {
    minimalTheme: theme
    bg
    text
    logo {
      ...ImageWith176pxWidth
    }
    alignment
    bgImage {
      ...ImageWithConstrainedLayout
    }
    links {
      ...Link
    }
    socialLinks {
      ...SocialLink
    }
    socialLinkStyles {
      ...SocialLinkStyles
    }
    newsletter {
      ...Newsletter
    }
    minimalFooterWidth: footerWidth
    useNewsletter
    minimalNewsletterPosition: newsletterPosition
    hideMorphicBadget
    minimalMorphicBadgeTier: morphicBadgeTier
    minimalBadgeBackground: badgeBackground
    minimalOverlayOpacity: overlayOpacity
  }
`
